import React, { useState } from "react";
import { Modal } from "../Modal/Modal";
import type { WalletOption } from "../../../constants/wallets";
import { walletOptions } from "../../../constants/wallets";
import { useWallet } from "../../../hooks/useWallet";
import { showCustomToast } from "../Toast/Toast";
import type { InjectedAccountWithMeta } from "@polkadot/extension-inject/types";
import { getWalletIcon } from "../ConnectedWalletDropdown/ConnectedWalletDropdown";
import ArrowRight from "../../../assets/images/arrowRight.svg";
import { Button } from "../Button";
import { useBalance, useWallet as useWalletConnect } from "@/contexts";
import { ArrowLeftIcon } from "lucide-react";
interface ConnectWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ConnectWalletModal: React.FC<ConnectWalletModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { connectWallet, isConnecting, getAccounts } = useWallet();
  const [selectedWallet, setSelectedWallet] = useState<WalletOption | null>(
    null
  );
  const [accounts, setAccounts] = useState<InjectedAccountWithMeta[]>([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const { setSelectedAccount } = useWalletConnect();
  const {isBalanceLoading} = useBalance()

  const getErrorMessage = (error: Error): string => {
    const errorMessage = error.message.toLowerCase();

    if (errorMessage.includes("not available")) {
      return "This wallet extension is not installed. Please install the required wallet extension first.";
    }

    if (errorMessage.includes("no accounts found")) {
      return "No accounts found in this wallet. Please create an account in your wallet extension first.";
    }

    if (errorMessage.includes("no wallet extension found")) {
      return "No wallet extensions found. Please install Subwallet, Talisman, or Polkadot.js extension.";
    }

    if (errorMessage.includes("not found or not available")) {
      return "Wallet extension not found. Please make sure the wallet extension is installed and enabled.";
    }

    return "Failed to connect wallet. Please try again.";
  };

  const handleWalletClick = async (wallet: WalletOption) => {
    try {
      setSelectedWallet(wallet);
      const walletAccounts = await getAccounts(wallet.provider);

      if (walletAccounts.length === 0) {
        showCustomToast(
          "error",
          "No accounts found in this wallet. Please create an account first."
        );
        return;
      }

      setAccounts(walletAccounts);
      setShowAccounts(true);
    } catch (error) {
      console.error("Failed to get accounts:", error);
      const errorMessage = getErrorMessage(error as Error);
      showCustomToast("error", errorMessage);
    }
  };

  const handleAccountSelect = async (account: InjectedAccountWithMeta) => {
    if (!selectedWallet) return;

    try {
      await connectWallet(selectedWallet.provider, account,setSelectedAccount);
      showCustomToast(
        "success",
        `Successfully connected to ${selectedWallet.name}!`
      );
      onClose();
      resetModal();
    } catch (error) {
      console.error("Failed to connect wallet:", error);
      const errorMessage = getErrorMessage(error as Error);
      showCustomToast("error", errorMessage);
    }
  };

  const handleBackClick = () => {
    setShowAccounts(false);
    setSelectedWallet(null);
    setAccounts([]);
  };

  const resetModal = () => {
    setShowAccounts(false);
    setSelectedWallet(null);
    setAccounts([]);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const truncateAddress = (address: string) => {
    if (address.length <= 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={showAccounts ? "Select Account" : "Connect Your Timechain Wallet"}
      size="md"
      className="max-w-[520px] w-full"
    >
      <div className="flex flex-col">
        {showAccounts && (
          <div className="flex items-center">
            <Button
              onClick={handleBackClick}
              variant="link"
              className="bg-transparent gap-1 !p-0"
            >
              <ArrowLeftIcon/>
              <span className="text-sm ml-0">Back</span>
            </Button>
          </div>
        )}

        {!showAccounts ? (
          <>
            <p className="text-sm text-left text-white/60 ">
              Select a wallet to connect and bridge your ANLOG tokens to Base.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              {walletOptions.map((wallet) => (
                <Button
                  key={wallet.id}
                  onClick={() => handleWalletClick(wallet)}
                  disabled={isConnecting}
                  variant="dropdown"
                  className="!p-3 hover:bg-modalInputBg hover:border-modalInputBorder"
                >
                  <div className="flex items-center justify-between w-full gap-2.5">
                    <div className="text-left w-full">
                      <div className="text-white text-sm font-medium capitalize">
                        {wallet.name}
                      </div>
                    </div>
                    <div className="min-w-8 w-8 h-8 flex items-center justify-center text-lg">
                      <img
                        src={getWalletIcon(wallet.provider)}
                        alt="Wallet Icon"
                      />
                    </div>
                  </div>
                  {/* <div className="text-white/40 group-hover:text-white transition-colors">

                                        <img src={ArrowRight} alt="Arrow Right" />
                                    </div> */}
                </Button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-left my-2">
              Select an account from {selectedWallet?.name}
            </p>

            <div className="space-y-3">
              {accounts.map((account, index) => (
                <Button
                  key={account.address}
                  onClick={() => handleAccountSelect(account)}
                  disabled={isConnecting}
                  variant="dropdown"
                  className="!p-3 hover:bg-modalInputBg hover:border-modalInputBorder"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center text-lg bg-accentPurple/20 rounded-full">
                      {index + 1}
                    </div>
                    <div className="text-left">
                      <div className="text-white font-medium">
                        {account.meta.name || `Account ${index + 1}`}
                      </div>
                      <div className="text-xs text-white/60">
                        {truncateAddress(account.address)}
                      </div>
                    </div>
                  </div>
                  <div className="text-white/40 group-hover:text-white transition-colors">
                    <img src={ArrowRight} alt="Arrow Right" />
                  </div>
                </Button>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
